import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { Button } from '../components/ui/button';
import { StarRating } from '../components/StarRating';
import { Comments } from '../components/Comments';
import { SEO, generateRecipeSchema, generateBreadcrumbSchema, injectJsonLd, removeJsonLd } from '../components/SEO';
import { ArrowLeft, Clock, Users, ChefHat, Share2, Facebook, Twitter, Link2, Check, ShoppingCart, Heart, Flame, Beef, Wheat, Droplet, Leaf } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecipeDetail = () => {
  const { id } = useParams();
  const { t, getLocalizedText, language } = useLanguage();
  const { user, token, isAuthenticated, updateFavorites } = useUser();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [ratingData, setRatingData] = useState({ average_rating: 0, rating_count: 0, user_rating: null });

  useEffect(() => {
    fetchRecipe();
    fetchRating();
  }, [id]);

  // SEO: Inject Recipe Schema when recipe is loaded
  useEffect(() => {
    if (recipe) {
      const recipeSchema = generateRecipeSchema(recipe, getLocalizedText, language);
      if (recipeSchema) {
        injectJsonLd(recipeSchema, 'recipe-schema');
      }
      
      // Breadcrumb schema
      const breadcrumbs = generateBreadcrumbSchema([
        { name: 'Home', url: 'https://love4vegan.com/' },
        { name: 'Recipes', url: 'https://love4vegan.com/recipes' },
        { name: getLocalizedText(recipe.title), url: `https://love4vegan.com/recipes/${id}` }
      ]);
      injectJsonLd(breadcrumbs, 'breadcrumb-schema');
    }
    
    return () => {
      removeJsonLd('recipe-schema');
      removeJsonLd('breadcrumb-schema');
    };
  }, [recipe, language, getLocalizedText, id]);

  useEffect(() => {
    if (user && user.favorites) {
      setIsFavorite(user.favorites.includes(id));
    }
  }, [user, id]);

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
    } catch (error) {
      console.error('Error fetching recipe:', error);
      setError('Recipe not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRating = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/ratings/${id}`, { headers });
      setRatingData(response.data);
    } catch (error) {
      console.error('Error fetching rating:', error);
    }
  };

  const handleRate = async (rating) => {
    if (!isAuthenticated) {
      toast.error(getLocalizedText({ fi: 'Kirjaudu arvostellaksesi', fr: 'Connectez-vous pour noter', en: 'Sign in to rate' }));
      return;
    }
    try {
      const response = await axios.post(
        `${API}/ratings`,
        { recipe_id: id, rating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRatingData(prev => ({
        ...prev,
        average_rating: response.data.average_rating,
        rating_count: response.data.rating_count,
        user_rating: rating
      }));
      toast.success(getLocalizedText({ fi: 'Arvostelu tallennettu!', fr: 'Note enregistrée!', en: 'Rating saved!' }));
    } catch (error) {
      toast.error('Failed to save rating');
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error(getLocalizedText({ fi: 'Kirjaudu lisätäksesi suosikkeihin', fr: 'Connectez-vous pour ajouter aux favoris', en: 'Sign in to add to favorites' }));
      return;
    }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        updateFavorites(id, false);
        toast.success(getLocalizedText({ fi: 'Poistettu suosikeista', fr: 'Retiré des favoris', en: 'Removed from favorites' }));
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        updateFavorites(id, true);
        toast.success(getLocalizedText({ fi: 'Lisätty suosikkeihin!', fr: 'Ajouté aux favoris!', en: 'Added to favorites!' }));
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const addToShoppingList = () => {
    const ingredients = getLocalizedText(recipe.ingredients)
      .split('\n')
      .map(line => line.replace(/^[-•*]\s*/, '').trim())
      .filter(line => line.length > 0);

    const saved = localStorage.getItem('love4vegan_shopping_list');
    const currentList = saved ? JSON.parse(saved) : [];
    
    const newItems = ingredients.map(item => ({
      id: Date.now() + Math.random(),
      text: item,
      checked: false,
      recipeId: recipe.id,
      recipeName: getLocalizedText(recipe.title)
    }));

    localStorage.setItem('love4vegan_shopping_list', JSON.stringify([...currentList, ...newItems]));
    toast.success(getLocalizedText({ 
      fi: `${ingredients.length} ainesta lisätty kauppalistalle!`, 
      fr: `${ingredients.length} ingrédients ajoutés à la liste!`, 
      en: `${ingredients.length} ingredients added to shopping list!` 
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-foreground/60 text-lg">{error || 'Recipe not found'}</p>
        <Link to="/recipes">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('recipeDetail.backToRecipes')}
          </Button>
        </Link>
      </div>
    );
  }

  const imageUrl = recipe.image_data || recipe.image_url || 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200';

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = getLocalizedText(recipe.title);

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(getLocalizedText({ fi: 'Linkki kopioitu!', fr: 'Lien copié!', en: 'Link copied!' }));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="min-h-screen py-8" data-testid="recipe-detail-page">
      {/* SEO Meta Tags */}
      <SEO 
        title={`${getLocalizedText(recipe.title)} - love4vegan`}
        description={getLocalizedText(recipe.description)}
        image={imageUrl}
        url={`https://love4vegan.com/recipes/${id}`}
        type="article"
        keywords={`${getLocalizedText(recipe.title)}, vegan recipe, vegaani resepti, recette végane`}
      />
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <Link to="/recipes" className="inline-block mb-8">
          <Button variant="ghost" className="text-foreground/60 hover:text-foreground" data-testid="back-to-recipes-btn">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('recipeDetail.backToRecipes')}
          </Button>
        </Link>

        {/* Hero Image */}
        <div className="relative rounded-3xl overflow-hidden aspect-[16/9] mb-8">
          <img
            src={imageUrl}
            alt={getLocalizedText(recipe.title)}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className="px-4 py-2 bg-secondary text-secondary-foreground text-sm font-medium rounded-full">
              {t(`recipes.${recipe.category}`)}
            </span>
            {recipe.featured && (
              <span className="px-4 py-2 bg-yellow-400 text-yellow-900 text-sm font-medium rounded-full">
                ⭐ {getLocalizedText({ fi: 'Suositeltu', fr: 'Vedette', en: 'Featured' })}
              </span>
            )}
          </div>
          {/* Action Buttons */}
          <div className="absolute top-4 right-4 flex gap-2">
            {/* Favorite Button */}
            <Button 
              variant="secondary" 
              size="icon" 
              className={`rounded-full shadow-lg ${isFavorite ? 'bg-secondary text-secondary-foreground' : 'bg-white/90 hover:bg-white text-foreground'}`}
              onClick={toggleFavorite}
              data-testid="favorite-button"
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
            {/* Share Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="rounded-full bg-white/90 hover:bg-white text-foreground shadow-lg"
                  data-testid="share-button"
                >
                  <Share2 className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => handleShare('facebook')} className="cursor-pointer">
                  <Facebook className="h-4 w-4 mr-2 text-[#1877F2]" />
                  Facebook
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer">
                  <Twitter className="h-4 w-4 mr-2 text-[#1DA1F2]" />
                  Twitter / X
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleShare('whatsapp')} className="cursor-pointer">
                  WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem onClick={copyLink} className="cursor-pointer">
                  {copied ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Link2 className="h-4 w-4 mr-2" />}
                  {getLocalizedText({ fi: 'Kopioi linkki', fr: 'Copier le lien', en: 'Copy link' })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Title & Meta */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="recipe-title">
            {getLocalizedText(recipe.title)}
          </h1>
          
          {/* Rating Section */}
          <div className="flex items-center gap-4 mb-4">
            <StarRating 
              rating={ratingData.user_rating || ratingData.average_rating} 
              onRate={handleRate}
              readonly={!isAuthenticated}
            />
            <span className="text-foreground/60">
              {ratingData.average_rating.toFixed(1)} ({ratingData.rating_count} {getLocalizedText({ fi: 'arvostelua', fr: 'avis', en: 'reviews' })})
            </span>
            {ratingData.user_rating && (
              <span className="text-sm text-primary">
                {getLocalizedText({ fi: 'Sinun arvostelusi:', fr: 'Votre note:', en: 'Your rating:' })} {ratingData.user_rating}★
              </span>
            )}
          </div>

          <p className="text-lg text-foreground/70 mb-6" data-testid="recipe-description">
            {getLocalizedText(recipe.description)}
          </p>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-full border border-border">
              <Clock className="h-5 w-5 text-primary" />
              <div>
                <span className="text-sm text-foreground/60">{t('recipeDetail.prepTime')}</span>
                <p className="font-medium">{recipe.prep_time} {t('recipes.minutes')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-full border border-border">
              <ChefHat className="h-5 w-5 text-secondary" />
              <div>
                <span className="text-sm text-foreground/60">{t('recipeDetail.cookTime')}</span>
                <p className="font-medium">{recipe.cook_time} {t('recipes.minutes')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-full border border-border">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <span className="text-sm text-foreground/60">{t('recipeDetail.servings')}</span>
                <p className="font-medium">{recipe.servings}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Nutrition Info */}
        {recipe.nutrition && (
          <div className="mb-8" data-testid="nutrition-info">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Leaf className="h-5 w-5 text-primary" />
              {getLocalizedText({ fi: 'Ravintotiedot (per annos)', fr: 'Informations nutritionnelles (par portion)', en: 'Nutrition Facts (per serving)' })}
            </h3>
            
            {/* Basic Macros */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-950/30 rounded-full border border-orange-200 dark:border-orange-900">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="font-medium">{recipe.nutrition.calories}</span>
                <span className="text-sm text-foreground/60">kcal</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-950/30 rounded-full border border-red-200 dark:border-red-900">
                <Beef className="h-4 w-4 text-red-500" />
                <span className="font-medium">{recipe.nutrition.protein}g</span>
                <span className="text-sm text-foreground/60">{getLocalizedText({ fi: 'proteiini', fr: 'protéines', en: 'protein' })}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 rounded-full border border-amber-200 dark:border-amber-900">
                <Wheat className="h-4 w-4 text-amber-500" />
                <span className="font-medium">{recipe.nutrition.carbs}g</span>
                <span className="text-sm text-foreground/60">{getLocalizedText({ fi: 'hiilihydraatit', fr: 'glucides', en: 'carbs' })}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/30 rounded-full border border-blue-200 dark:border-blue-900">
                <Droplet className="h-4 w-4 text-blue-500" />
                <span className="font-medium">{recipe.nutrition.fat}g</span>
                <span className="text-sm text-foreground/60">{getLocalizedText({ fi: 'rasva', fr: 'lipides', en: 'fat' })}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-950/30 rounded-full border border-green-200 dark:border-green-900">
                <Leaf className="h-4 w-4 text-green-500" />
                <span className="font-medium">{recipe.nutrition.fiber}g</span>
                <span className="text-sm text-foreground/60">{getLocalizedText({ fi: 'kuitu', fr: 'fibres', en: 'fiber' })}</span>
              </div>
            </div>

            {/* Vitamins & Minerals */}
            {recipe.nutrition.vitamins && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vitamins */}
                <div className="bg-purple-50 dark:bg-purple-950/20 rounded-xl p-4 border border-purple-200 dark:border-purple-900">
                  <h4 className="font-medium text-purple-700 dark:text-purple-300 mb-3">
                    {getLocalizedText({ fi: 'Vitamiinit', fr: 'Vitamines', en: 'Vitamins' })}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'C-vitamiini', fr: 'Vitamine C', en: 'Vitamin C' })}</span>
                      <span className="font-medium">{recipe.nutrition.vitamins.vitaminC?.amount || 0} mg <span className="text-purple-500">({recipe.nutrition.vitamins.vitaminC?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'A-vitamiini', fr: 'Vitamine A', en: 'Vitamin A' })}</span>
                      <span className="font-medium">{recipe.nutrition.vitamins.vitaminA?.amount || 0} mcg <span className="text-purple-500">({recipe.nutrition.vitamins.vitaminA?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'D-vitamiini', fr: 'Vitamine D', en: 'Vitamin D' })}</span>
                      <span className="font-medium">{recipe.nutrition.vitamins.vitaminD?.amount || 0} mcg <span className="text-purple-500">({recipe.nutrition.vitamins.vitaminD?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'B12-vitamiini', fr: 'Vitamine B12', en: 'Vitamin B12' })}</span>
                      <span className="font-medium">{recipe.nutrition.vitamins.vitaminB12?.amount || 0} mcg <span className="text-purple-500">({recipe.nutrition.vitamins.vitaminB12?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'Folaatti', fr: 'Folate', en: 'Folate' })}</span>
                      <span className="font-medium">{recipe.nutrition.vitamins.folate?.amount || 0} mcg <span className="text-purple-500">({recipe.nutrition.vitamins.folate?.dailyPercent || 0}%)</span></span>
                    </div>
                  </div>
                </div>

                {/* Minerals */}
                <div className="bg-teal-50 dark:bg-teal-950/20 rounded-xl p-4 border border-teal-200 dark:border-teal-900">
                  <h4 className="font-medium text-teal-700 dark:text-teal-300 mb-3">
                    {getLocalizedText({ fi: 'Kivennäisaineet', fr: 'Minéraux', en: 'Minerals' })}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'Rauta', fr: 'Fer', en: 'Iron' })}</span>
                      <span className="font-medium">{recipe.nutrition.minerals?.iron?.amount || 0} mg <span className="text-teal-500">({recipe.nutrition.minerals?.iron?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'Kalsium', fr: 'Calcium', en: 'Calcium' })}</span>
                      <span className="font-medium">{recipe.nutrition.minerals?.calcium?.amount || 0} mg <span className="text-teal-500">({recipe.nutrition.minerals?.calcium?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'Sinkki', fr: 'Zinc', en: 'Zinc' })}</span>
                      <span className="font-medium">{recipe.nutrition.minerals?.zinc?.amount || 0} mg <span className="text-teal-500">({recipe.nutrition.minerals?.zinc?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'Magnesium', fr: 'Magnésium', en: 'Magnesium' })}</span>
                      <span className="font-medium">{recipe.nutrition.minerals?.magnesium?.amount || 0} mg <span className="text-teal-500">({recipe.nutrition.minerals?.magnesium?.dailyPercent || 0}%)</span></span>
                    </div>
                    <div className="flex justify-between">
                      <span>{getLocalizedText({ fi: 'Kalium', fr: 'Potassium', en: 'Potassium' })}</span>
                      <span className="font-medium">{recipe.nutrition.minerals?.potassium?.amount || 0} mg <span className="text-teal-500">({recipe.nutrition.minerals?.potassium?.dailyPercent || 0}%)</span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Disclaimer */}
            <p className="text-xs text-foreground/40 mt-3">
              {getLocalizedText({ 
                fi: '* Arvot ovat arvioita. % päivittäisestä saantisuosituksesta.', 
                fr: '* Les valeurs sont des estimations. % de l\'apport journalier recommandé.', 
                en: '* Values are estimates. % of daily recommended intake.' 
              })}
            </p>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Ingredients */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-2xl p-6 border border-border sticky top-24">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">1</span>
                {t('recipeDetail.ingredients')}
              </h2>
              
              <div 
                className="text-foreground/80 leading-relaxed whitespace-pre-line mb-4"
                data-testid="recipe-ingredients"
              >
                {getLocalizedText(recipe.ingredients)}
              </div>

              {/* Add to Shopping List */}
              <Button
                onClick={addToShoppingList}
                variant="outline"
                className="w-full rounded-full"
                data-testid="add-to-shopping-list-btn"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {getLocalizedText({ fi: 'Lisää kauppalistalle', fr: 'Ajouter à la liste', en: 'Add to shopping list' })}
              </Button>
            </div>
          </div>

          {/* Instructions & Comments */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-card rounded-2xl p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold">2</span>
                {t('recipeDetail.instructions')}
              </h2>
              <div 
                className="text-foreground/80 leading-relaxed whitespace-pre-line"
                data-testid="recipe-instructions"
              >
                {getLocalizedText(recipe.instructions)}
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <Comments recipeId={id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
